import type { Document, DownloadFileRequest } from '@/generated-api'
import { useDocumentsStore, useLocalizationStore, useUserStore } from '@/stores'
import { sortArrayAlphabetically, sortArrayByDate } from '@/application/helpers'
import type { SelectItem } from '@/interfaces/components/ISelectComponent'

export const getBadgeText = (type: string): string => {
  const documentStore = useDocumentsStore()
  const types = documentStore.documentTypes()

  if (types !== undefined) {
    switch (type) {
      case 'CV':
        return types?.find((type: SelectItem) => type.id === 'CV')!.name
      case 'Consent':
        return types?.find((type: SelectItem) => type.id === 'Consent')!.name
      case 'Application':
        return types?.find((type: SelectItem) => type.id === 'Application')!.name
      case 'Miscellaneous':
        return types?.find((type: SelectItem) => type.id === 'Miscellaneous')!.name
      default:
        return type
    }
  }
  return ''
}

export const getBadgeColor = (type: string): string => {
  switch (type) {
    case 'CV':
      return 'green'
    case 'Consent':
      return 'yellow'
    case 'Application':
      return 'blue'
    case 'Miscellaneous':
      return 'gray'
    default:
      return ''
  }
}

export const isDocumentImageFile = (extension: string) => {
  const imageFileTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'tiff', 'svg']

  if (extension !== undefined && imageFileTypes.includes(extension.split('.').pop()!)) {
    return true
  }

  return false
}

export const sortList = (listToSort: Document[] | undefined, sortType: string): Document[] => {
  if (!listToSort || !listToSort.length || !sortType) {
    return []
  }

  switch (sortType) {
    case 'title':
      return sortArrayAlphabetically(listToSort, 'title')

    case 'date':
      return sortArrayByDate(listToSort, 'date').reverse()

    case 'type':
      return sortByDocumentType(listToSort)

    default:
      return listToSort
  }
}

export const getRequestParameters = (
  programmeId: string,
  documentId: string
): DownloadFileRequest => {
  return {
    siteKey: 'programme_' + programmeId,
    documentId: documentId
  }
}

export const getDropDownItems = (document: Document) => {
  const localicationStore = useLocalizationStore()
  const documentStore = useDocumentsStore()
  const userStore = useUserStore()
  const proxyLogin = userStore.member.isCandidateProxyLogin

  return [
    {
      id: 'download',
      name: localicationStore.translate('general_Download'),
      icon: 'cloud-arrow-down'
    },
    ...(isDocumentImageFile(document.extension!)
      ? [
          {
            id: 'previewimage',
            name: documentStore.seeImagePreviewText,
            icon: 'image'
          }
        ]
      : []),
    ...(document.canEdit && proxyLogin !== true
      ? [
          {
            name: localicationStore.translate('general_Edit'),
            id: 'edit',
            icon: 'pencil'
          }
        ]
      : []),
    ...(document.canDelete && proxyLogin !== true
      ? [
          {
            id: 'delete',
            name: localicationStore.translate('general_Delete'),
            icon: 'trash'
          }
        ]
      : []),
    ...(document.id !== documentStore.publicDocumentId && document.type === 'CV' && document.canEdit
      ? [
          {
            id: 'addcv',
            name: documentStore.addCvText,
            icon: 'plus'
          }
        ]
      : [])
  ]
}

export const sortByDocumentType = (list: Document[]): Document[] => {
  return list.sort((a, b) => {
    const typeA = a?.type || ''
    const typeB = b?.type || ''

    const translatedNameA = getBadgeText(typeA)
    const translatedNameB = getBadgeText(typeB)

    return translatedNameA.localeCompare(translatedNameB)
  })
}
